import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../firebase";
import { useAuth } from "../../contexts/AuthContext";
import { Button } from "../../design/button";
import { Heading, Subheading } from "../../design/heading";
import { Text, Strong } from "../../design/text";
import {
  BuildingOffice2Icon,
  CheckCircleIcon,
  ArrowRightIcon,
  UserGroupIcon,
  CalendarDaysIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/outline";
import LoadingView from "../../components/LoadingView";
import LogoHorizontal from "../../components/LogoHorizontal";

interface PendingInviteInfo {
  accountId: string;
  accountName: string;
  inviteId: string;
  memberType: "staff" | "supervisor";
  invitedByName: string;
  expiresAt: number;
}

type PageState = "loading" | "ready" | "accepting" | "invite-accepted";

const features = [
  {
    icon: CalendarDaysIcon,
    title: "Schedule & Manage Visits",
    description: "Create, track, and manage supervised visitation schedules with ease.",
  },
  {
    icon: UserGroupIcon,
    title: "Coordinate Participants",
    description: "Organize supervisors, parents, and children all in one place.",
  },
  {
    icon: DocumentTextIcon,
    title: "Generate Reports",
    description: "Create professional visit reports and documentation automatically.",
  },
  {
    icon: CheckCircleIcon,
    title: "Track Compliance",
    description: "Monitor visit attendance, incidents, and case progress.",
  },
];

const WelcomePage: React.FC = () => {
  const navigate = useNavigate();
  const { user, refreshClaims } = useAuth();

  const [pageState, setPageState] = useState<PageState>("loading");
  const [pendingInvites, setPendingInvites] = useState<PendingInviteInfo[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [acceptedCount, setAcceptedCount] = useState(0);

  // Check for pending invites on mount
  useEffect(() => {
    if (!user?.email) {
      setPageState("ready");
      return;
    }

    const checkInvites = async () => {
      try {
        const checkPendingInvites = httpsCallable<
          { email: string },
          { invites: PendingInviteInfo[] }
        >(functions, "checkPendingInvites");

        const result = await checkPendingInvites({ email: user.email! });
        setPendingInvites(result.data.invites);
        setPageState("ready");
      } catch (err) {
        console.error("Error checking for invites:", err);
        setPageState("ready");
      }
    };

    checkInvites();
  }, [user?.email]);

  // Accept all pending invites
  const handleAcceptInvites = useCallback(async () => {
    if (!user?.uid || pendingInvites.length === 0) return;

    setPageState("accepting");
    setError(null);

    try {
      const acceptInvites = httpsCallable<
        { invites: Array<{ accountId: string; inviteId: string }> },
        { success: boolean; acceptedCount: number }
      >(functions, "acceptInvitesForUser");

      const result = await acceptInvites({
        invites: pendingInvites.map((inv) => ({
          accountId: inv.accountId,
          inviteId: inv.inviteId,
        })),
      });

      setAcceptedCount(result.data.acceptedCount);
      await refreshClaims();
      setPageState("invite-accepted");
    } catch (err: any) {
      console.error("Error accepting invites:", err);
      setError(err.message || "Failed to accept invites. Please try again.");
      setPageState("ready");
    }
  }, [user?.uid, pendingInvites, refreshClaims]);

  // Continue to onboarding (business setup)
  const handleSetupBusiness = useCallback(() => {
    navigate("/onboarding");
  }, [navigate]);

  // Go to dashboard (for users who accepted invites)
  const handleGoToDashboard = useCallback(() => {
    navigate("/dashboard");
  }, [navigate]);

  const formatExpiration = (timestamp: number) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffDays = Math.ceil(
      (date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
    );

    if (diffDays <= 0) return "Expired";
    if (diffDays === 1) return "Expires tomorrow";
    if (diffDays <= 7) return `Expires in ${diffDays} days`;
    return `Expires ${date.toLocaleDateString()}`;
  };

  if (pageState === "loading") {
    return <LoadingView />;
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 dark:from-zinc-900 dark:via-zinc-800 dark:to-zinc-900">
      {/* Decorative background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-200 rounded-full opacity-20 blur-3xl dark:bg-blue-900" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-indigo-200 rounded-full opacity-20 blur-3xl dark:bg-indigo-900" />
      </div>

      <div className="relative w-full max-w-2xl px-4">
        <div className="w-full p-8 bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 dark:bg-zinc-900/80 dark:border-zinc-700/50">
          {error && (
            <div className="p-4 mb-6 text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl dark:bg-red-900/30 dark:text-red-400 dark:border-red-800/50">
              {error}
            </div>
          )}

          {/* Accepting Invites */}
          {pageState === "accepting" && (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto mb-4" />
              <Text className="text-zinc-600 dark:text-zinc-400">
                Accepting your invitations...
              </Text>
            </div>
          )}

          {/* Invite Accepted - Success State */}
          {pageState === "invite-accepted" && (
            <div className="flex flex-col items-center gap-6">
              <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-green-100 dark:bg-green-900/50">
                <CheckCircleIcon className="w-12 h-12 text-green-600 dark:text-green-400" />
              </div>
              <div className="text-center">
                <Heading className="mb-2">You're All Set!</Heading>
                <Text className="text-zinc-600 dark:text-zinc-400">
                  You've joined {acceptedCount} organization{acceptedCount > 1 ? "s" : ""}.
                  <br />
                  You now have access to your team's dashboard.
                </Text>
              </div>
              <Button onClick={handleGoToDashboard} color="green" className="w-full max-w-xs">
                Go to Dashboard
                <ArrowRightIcon className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}

          {/* Main Welcome State */}
          {pageState === "ready" && (
            <div className="flex flex-col items-center gap-6">
              {/* Header */}
              <div className="flex flex-col items-center gap-2">
                <LogoHorizontal />
                <Heading className="mt-4 text-center">
                  Welcome to VisitProof
                </Heading>
                <Text className="max-w-md text-center text-zinc-600 dark:text-zinc-400">
                  Your complete platform for managing supervised visitations with confidence and clarity.
                </Text>
              </div>

              {/* Pending Invitations Section */}
              {pendingInvites.length > 0 && (
                <div className="w-full p-4 border-2 border-green-200 rounded-xl bg-green-50/50 dark:bg-green-900/10 dark:border-green-800">
                  <div className="flex items-center gap-2 mb-3">
                    <UserGroupIcon className="w-5 h-5 text-green-600 dark:text-green-400" />
                    <Subheading level={3} className="text-green-800 dark:text-green-300">
                      You have {pendingInvites.length} pending invitation{pendingInvites.length > 1 ? "s" : ""}
                    </Subheading>
                  </div>
                  <div className="space-y-2 mb-4">
                    {pendingInvites.map((invite) => (
                      <div
                        key={`${invite.accountId}-${invite.inviteId}`}
                        className="flex items-center justify-between p-3 bg-white rounded-lg dark:bg-zinc-800/50"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 flex items-center justify-center rounded-full bg-green-100 dark:bg-green-800/50">
                            <BuildingOffice2Icon className="w-4 h-4 text-green-600 dark:text-green-400" />
                          </div>
                          <div>
                            <p className="font-medium text-sm text-zinc-900 dark:text-white">
                              {invite.accountName}
                            </p>
                            <p className="text-xs text-zinc-500 dark:text-zinc-400">
                              {invite.memberType} · {formatExpiration(invite.expiresAt)}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <Button
                    onClick={handleAcceptInvites}
                    color="green"
                    className="w-full"
                  >
                    Accept Invitation{pendingInvites.length > 1 ? "s" : ""} & Go to Dashboard
                  </Button>
                </div>
              )}

              {/* Divider if there are invites */}
              {pendingInvites.length > 0 && (
                <div className="flex items-center w-full gap-4">
                  <div className="flex-1 h-px bg-zinc-200 dark:bg-zinc-700" />
                  <Text className="text-sm text-zinc-500">or</Text>
                  <div className="flex-1 h-px bg-zinc-200 dark:bg-zinc-700" />
                </div>
              )}

              {/* Setup Business Section */}
              <div className="w-full">
                <div className="flex items-center gap-2 mb-4">
                  <BuildingOffice2Icon className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                  <Subheading level={3}>
                    Set Up Your Business
                  </Subheading>
                </div>

                <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2">
                  {features.map((feature) => (
                    <div
                      key={feature.title}
                      className="flex gap-3 p-3 rounded-lg bg-zinc-50 dark:bg-zinc-800/50"
                    >
                      <div className="flex items-center justify-center flex-shrink-0 w-8 h-8 rounded-lg bg-indigo-100 dark:bg-indigo-900/30">
                        <feature.icon className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                      </div>
                      <div>
                        <Text className="text-sm font-medium text-zinc-900 dark:text-white">
                          {feature.title}
                        </Text>
                        <Text className="text-xs text-zinc-500 dark:text-zinc-400">
                          {feature.description}
                        </Text>
                      </div>
                    </div>
                  ))}
                </div>

                <Button
                  onClick={handleSetupBusiness}
                  className="w-full mt-4"
                >
                  Start Business Setup
                  <ArrowRightIcon className="w-4 h-4 ml-2" />
                </Button>
              </div>

              {/* Help Section */}
              <div className="w-full p-4 border rounded-lg bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 mt-0.5">
                    <svg className="w-5 h-5 text-amber-600 dark:text-amber-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <Strong className="text-sm text-amber-800 dark:text-amber-200">
                      Need help getting started?
                    </Strong>
                    <Text className="mt-1 text-sm text-amber-700 dark:text-amber-300">
                      Schedule a free onboarding session at{" "}
                      <a
                        href="mailto:info@visitproof.com"
                        className="font-medium underline hover:no-underline"
                      >
                        info@visitproof.com
                      </a>
                    </Text>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <p className="mt-6 text-center text-xs text-zinc-500 dark:text-zinc-500">
          Need help?{" "}
          <a
            href="mailto:support@visitproof.com"
            className="underline hover:text-zinc-700 dark:hover:text-zinc-300"
          >
            Contact support
          </a>
        </p>
      </div>
    </div>
  );
};

export default WelcomePage;
